import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, User, ChevronDown } from "lucide-react";
import AnimatedLogo from "./AnimatedLogo";

const locationLinks = [
  { path: "/double-glazing-hamilton", label: "Double Glazing Hamilton" },
  { path: "/double-glazing-motherwell", label: "Double Glazing Motherwell" },
  { path: "/double-glazing-east-kilbride", label: "Double Glazing East Kilbride" },
  { path: "/double-glazing-airdrie", label: "Double Glazing Airdrie" },
  { path: "/double-glazing-coatbridge", label: "Double Glazing Coatbridge" },
  { path: "/double-glazing-wishaw", label: "Double Glazing Wishaw" },
  { path: "/double-glazing-bellshill", label: "Double Glazing Bellshill" },
  { path: "/double-glazing-glasgow", label: "Double Glazing Glasgow" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileWindowsOpen, setMobileWindowsOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setMobileWindowsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/windows", label: "Windows", hasDropdown: true },
    { path: "/doors", label: "Doors" },
    { path: "/outbuilds", label: "Outbuilds" },
    { path: "/recent-work", label: "Recent Work" },
    { path: "/videos", label: "Videos" },
    { path: "/reviews", label: "Reviews" },
    { path: "/faq", label: "FAQ" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;
  const isWindowsSection = location.pathname === "/windows" || location.pathname.startsWith("/double-glazing-");

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#0A0A0A] ${
        isScrolled ? "shadow-lg shadow-black/30" : ""
      }`}
    >
      {/* Top bar with phone */}
      <div className="bg-white py-2 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <p className="text-[#0A0A0A] text-sm font-medium hidden sm:block">
            Serving Lanarkshire, Glasgow & Surrounding Areas
          </p>
          <div className="flex items-center space-x-6">
            <a
              href="tel:07917181597"
              data-testid="header-phone-top"
              className="flex items-center space-x-2 text-[#0A0A0A] font-bold text-sm hover:text-[#FF6B00] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>07917 181597</span>
              <span className="hidden sm:inline">- Free Quotation No Obligation</span>
            </a>
            <Link
              to="/admin"
              data-testid="header-admin-link"
              className="flex items-center space-x-1 text-[#525252] text-sm hover:text-[#FF6B00] transition-colors"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <AnimatedLogo size="large" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" data-testid="desktop-nav">
            <div className="flex items-center bg-[#1A1A1A]">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => link.hasDropdown && setIsDropdownOpen(true)}
                  onMouseLeave={() => link.hasDropdown && setIsDropdownOpen(false)}
                >
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                    className={`relative flex items-center gap-1 px-5 py-3 font-semibold text-sm uppercase tracking-wide transition-all ${
                      link.hasDropdown && isWindowsSection
                        ? "bg-[#FF6B00] text-white"
                        : isActive(link.path)
                        ? "bg-[#FF6B00] text-white"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {link.label}
                    {link.hasDropdown && <ChevronDown className="w-3.5 h-3.5" />}
                  </Link>
                  
                  {/* Dropdown */}
                  {link.hasDropdown && isDropdownOpen && (
                    <div className="absolute top-full left-0 w-64 bg-[#1A1A1A] shadow-xl border-t-2 border-[#FF6B00] z-50">
                      {locationLinks.map((loc) => (
                        <Link
                          key={loc.path}
                          to={loc.path}
                          data-testid={`dropdown-${loc.path.slice(1)}`}
                          className={`block px-5 py-3 text-sm font-medium transition-colors ${
                            isActive(loc.path)
                              ? "bg-[#FF6B00] text-white"
                              : "text-gray-300 hover:text-white hover:bg-[#FF6B00]/20"
                          }`}
                        >
                          {loc.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <Link
              to="/contact"
              data-testid="header-cta"
              className="group relative bg-[#FF6B00] text-white font-bold uppercase text-sm tracking-wide px-8 py-4 overflow-hidden transition-all hover:shadow-lg hover:shadow-orange-500/30"
            >
              <span className="relative z-10">Get Quote</span>
              <div className="absolute inset-0 bg-[#E65000] transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-0 top-[116px] bg-[#0A0A0A] z-40 transition-transform duration-300 overflow-y-auto ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-2">
          {navLinks.map((link) => (
            <div key={link.path}>
              {link.hasDropdown ? (
                <>
                  <div className="flex items-center">
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                      className={`flex-1 text-lg font-bold uppercase tracking-wide py-4 px-4 transition-colors ${
                        isWindowsSection
                          ? "bg-[#FF6B00] text-white"
                          : "text-gray-300 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {link.label}
                    </Link>
                    <button
                      onClick={() => setMobileWindowsOpen(!mobileWindowsOpen)}
                      className="px-4 py-4 text-gray-300"
                      data-testid="mobile-windows-toggle"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform ${mobileWindowsOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {mobileWindowsOpen && (
                    <div className="ml-4 space-y-1">
                      {locationLinks.map((loc) => (
                        <Link
                          key={loc.path}
                          to={loc.path}
                          data-testid={`mobile-${loc.path.slice(1)}`}
                          className={`block text-sm font-medium py-3 px-4 transition-colors ${
                            isActive(loc.path)
                              ? "text-[#FF6B00]"
                              : "text-gray-400 hover:text-white"
                          }`}
                        >
                          {loc.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  className={`text-lg font-bold uppercase tracking-wide py-4 px-4 transition-colors block ${
                    isActive(link.path)
                      ? "bg-[#FF6B00] text-white"
                      : "text-gray-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
          <div className="pt-6 border-t border-gray-800 mt-4">
            <a
              href="tel:07917181597"
              data-testid="mobile-phone"
              className="flex items-center space-x-3 text-[#FF6B00] py-3"
            >
              <Phone className="w-6 h-6" />
              <span className="font-bold text-lg">07917 181597</span>
            </a>
            <Link
              to="/admin"
              data-testid="mobile-admin-link"
              className="flex items-center space-x-3 text-gray-400 py-3 hover:text-white"
            >
              <User className="w-6 h-6" />
              <span className="font-bold">Admin Login</span>
            </Link>
            <Link
              to="/contact"
              data-testid="mobile-cta"
              className="block bg-[#FF6B00] text-white font-bold uppercase text-center py-4 mt-4 text-lg"
            >
              Get Free Quote
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
